import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Checkbox, Input, Button, Divider, Spin, Empty, Space, Tag } from 'antd';
import { PrinterOutlined, SaveOutlined } from '@ant-design/icons';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../../variables/api/axiosInstance';
import MandatInvestigation from '../../../docs/MandatInvestigation';

/**
 * Sélection et impression du Mandat d'Investigation pour une expertise (investigateur).
 * Charge le catalogue paramétrable (rubriques + points), pré-coche la sélection déjà
 * enregistrée sur l'expertise, permet de cocher/décocher et de saisir les lieux, sauvegarde
 * (JSON sur l'expertise) puis imprime le document (uniquement les points cochés).
 */
/**
 * `sinistreMode` : le mandat est rattaché AU DOSSIER (Sinistre) et non à une Expertise —
 * voie légère d'affectation d'un investigateur (sans victime). Lecture/écriture sur le sinistre.
 */
const MandatInvestigationModal = ({ open, setOpen, expertise, sinistre, sinistreMode = false, investigateur, onSaved }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rubriques, setRubriques] = useState([]);
    const [points, setPoints] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [lieux, setLieux] = useState({});
    const [introTemplate, setIntroTemplate] = useState(null);
    const [directeurNom, setDirecteurNom] = useState(null);
    const [avecEntete, setAvecEntete] = useState(true);
    const printRef = useRef(null);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `@page { size: A4 portrait !important; margin: 20mm; }
            @media print { html, body { width: 210mm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`,
        documentTitle: "Mandat d'investigation",
    });

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        Promise.all([
            axiosInstance.get('/api/investigation/rubriques'),
            axiosInstance.get('/api/investigation/points'),
            axiosInstance.get('/api/investigation/config'),
        ])
            .then(([r, p, c]) => {
                setRubriques(Array.isArray(r.data) ? r.data : []);
                setPoints(Array.isArray(p.data) ? p.data : []);
                setIntroTemplate(c?.data?.introTemplate || null);
                setDirecteurNom(c?.data?.directeurNom || null);
            })
            .catch(() => toast.error("Erreur lors du chargement du catalogue d'investigation"))
            .finally(() => setLoading(false));

        // Pré-remplissage de la sélection déjà enregistrée (dossier OU expertise selon le mode).
        try {
            const source = sinistreMode ? sinistre?.mandatInvestigationJson : expertise?.mandatInvestigationJson;
            const parsed = source ? JSON.parse(source) : null;
            setSelected(new Set(Array.isArray(parsed?.points) ? parsed.points : []));
            setLieux(parsed?.lieux && typeof parsed.lieux === 'object' ? parsed.lieux : {});
        } catch (_) {
            setSelected(new Set());
            setLieux({});
        }
    }, [open, expertise, sinistre, sinistreMode]);

    const pointsParRubrique = useMemo(() => {
        const map = {};
        points.forEach((p) => {
            (map[p.rubriqueCodeId] = map[p.rubriqueCodeId] || []).push(p);
        });
        Object.values(map).forEach((arr) => arr.sort((a, b) => (a.ordre || 0) - (b.ordre || 0)));
        return map;
    }, [points]);

    const togglePoint = (codeId, checked) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(codeId); else next.delete(codeId);
            return next;
        });
    };

    const toggleRubrique = (rubriqueCodeId, checked) => {
        const ids = (pointsParRubrique[rubriqueCodeId] || []).map((p) => p.codeId);
        setSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
            return next;
        });
    };

    // Rubriques résolues pour le document : seuls les points cochés, rubriques non vides.
    const rubriquesRendues = useMemo(() => rubriques
        .map((r) => ({
            intitule: r.intitule,
            demandeLieu: r.demandeLieu,
            lieu: lieux[r.codeId] || '',
            points: (pointsParRubrique[r.codeId] || []).filter((p) => selected.has(p.codeId)).map((p) => p.intitule),
        }))
        .filter((r) => r.points.length > 0), [rubriques, pointsParRubrique, selected, lieux]);

    const buildJson = () => JSON.stringify({ points: Array.from(selected), lieux });

    const handleSave = async () => {
        const targetId = sinistreMode ? sinistre?.codeId : expertise?.codeId;
        if (!targetId) { toast.error(sinistreMode ? "Dossier introuvable" : "Expertise introuvable"); return; }
        setSaving(true);
        try {
            const url = sinistreMode
                ? `/api/sinistre/${sinistre.codeId}/mandat-investigation`
                : `/api/expertise/${expertise.codeId}/mandat-investigation`;
            await axiosInstance.put(url, { mandatInvestigationJson: buildJson() });
            toast.success('Sélection du mandat enregistrée');
            if (onSaved) onSaved();
        } catch (_) {
            toast.error("Échec de l'enregistrement de la sélection");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={<span style={{ color: '#242166' }}>Mandat d'investigation — points à vérifier</span>}
            open={open}
            onCancel={() => setOpen(false)}
            footer={null}
            width={1100}
            zIndex={2000}
            centered
            styles={{ body: { maxHeight: '76vh', overflowY: 'auto' } }}
        >
            <Spin spinning={loading}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    {/* Colonne sélection */}
                    <div>
                        <p style={{ color: '#64748b', marginBottom: 12 }}>
                            Cochez les points applicables à ce dossier. Seuls les points cochés apparaîtront sur le mandat.
                        </p>
                        {rubriques.length === 0 && !loading && (
                            <Empty description="Aucune rubrique paramétrée" />
                        )}
                        {rubriques.map((r) => {
                            const pts = pointsParRubrique[r.codeId] || [];
                            const allChecked = pts.length > 0 && pts.every((p) => selected.has(p.codeId));
                            const someChecked = pts.some((p) => selected.has(p.codeId));
                            return (
                                <div key={r.codeId} style={{ marginBottom: 14, border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                                    <Checkbox
                                        checked={allChecked}
                                        indeterminate={!allChecked && someChecked}
                                        onChange={(e) => toggleRubrique(r.codeId, e.target.checked)}
                                        style={{ fontWeight: 700, color: '#242166' }}
                                    >
                                        {r.intitule || 'Vérifications générales'}
                                    </Checkbox>
                                    {r.demandeLieu && (
                                        <Input
                                            size="small"
                                            placeholder="Préciser le lieu (ex. Pessidé, CHP Kanté…)"
                                            value={lieux[r.codeId] || ''}
                                            onChange={(e) => setLieux((prev) => ({ ...prev, [r.codeId]: e.target.value }))}
                                            style={{ margin: '8px 0', maxWidth: 320 }}
                                        />
                                    )}
                                    <div style={{ paddingLeft: 24, marginTop: 6 }}>
                                        {pts.map((p) => (
                                            <div key={p.codeId} style={{ marginBottom: 4 }}>
                                                <Checkbox
                                                    checked={selected.has(p.codeId)}
                                                    onChange={(e) => togglePoint(p.codeId, e.target.checked)}
                                                >
                                                    {p.intitule}
                                                </Checkbox>
                                            </div>
                                        ))}
                                        {pts.length === 0 && <span style={{ color: '#94a3b8', fontSize: 12 }}>Aucun point</span>}
                                    </div>
                                </div>
                            );
                        })}

                        <Space style={{ marginTop: 8 }}>
                            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave} style={{ background: '#242166' }}>
                                Enregistrer la sélection
                            </Button>
                            <Tag color="blue">{selected.size} point(s) coché(s)</Tag>
                        </Space>
                    </div>

                    {/* Colonne aperçu + impression */}
                    <div>
                        <Space style={{ marginBottom: 8 }}>
                            <Checkbox checked={avecEntete} onChange={(e) => setAvecEntete(e.target.checked)}>
                                Imprimer l'en-tête et le pied de page
                            </Checkbox>
                            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                                Imprimer
                            </Button>
                        </Space>
                        <Divider style={{ margin: '8px 0' }} />
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, transform: 'scale(0.85)', transformOrigin: 'top left', width: '117%' }}>
                            <MandatInvestigation
                                ref={printRef}
                                expertise={expertise}
                                investigateur={sinistreMode ? investigateur : undefined}
                                sinistre={sinistre}
                                rubriques={rubriquesRendues}
                                introTemplate={introTemplate}
                                directeurNom={directeurNom}
                                afficherEnteteEtPied={avecEntete}
                            />
                        </div>
                    </div>
                </div>
            </Spin>
        </Modal>
    );
};

MandatInvestigationModal.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    expertise: PropTypes.object,
    sinistre: PropTypes.object,
    sinistreMode: PropTypes.bool,
    investigateur: PropTypes.object,
    onSaved: PropTypes.func,
};

export default MandatInvestigationModal;
